import { GeckoFeatureModelType } from 'types/assetModel/assetModelEnums'
import { installationDateFormField } from 'types/assetModel/commonFields'
import {
  FeatureTableOptions,
  GeckoEditableFeature,
} from 'types/assetModel/features/baseClasses/geckoEditableFeature'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoComponentRect } from 'types/geometric/geckoComponentRect'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import { parseDate, parseString } from 'utils/parsingHelpers'
import {
  optionalNumberSchema,
  optionalStringSchema,
  requiredNumberSchema,
  requiredStringSchema,
} from 'utils/zodHelpers'

import { validateTubeMeasurement } from './geckoLegacyBoilerWall'

//------------------------------------------------------------------------------
// TODO ARCH-144: make me an ENUM
export type LegacyBoilerRepairType = 'overlay' | 'panel'

enum LegacyBoilerType {
  Overlay = 'overlay',
  Panel = 'panel',
  Unknown = 'unknown',
}

export interface GeckoLegacyBoilerRepairRegionJSON {
  type: GeckoFeatureModelType.LegacyBoilerRepairRegion
  name: string
  feature_geometry: {
    tube_start: number // in tubes, inclusive,
    y_start: number // in meters,
    tube_end: number // in tubes, inclusive,
    y_end: number // in meters,
  }
  surface_specs: {
    wall_nominal_thickness: number | undefined // in meters
    coating_nominal_thickness: number | undefined // in meters -- the thickness of a coating applied (not including base thickness)
  }
  installation_date: string | undefined
  repair_type: LegacyBoilerRepairType | undefined
}

export const NewGeckoLegacyBoilerRepairRegionJSON: GeckoLegacyBoilerRepairRegionJSON =
  {
    type: GeckoFeatureModelType.LegacyBoilerRepairRegion,
    name: 'New Repair Region',
    feature_geometry: {
      tube_start: 0,
      y_start: 0,
      tube_end: 0,
      y_end: 0,
    },
    surface_specs: {
      wall_nominal_thickness: undefined,
      coating_nominal_thickness: undefined,
    },
    installation_date: undefined,
    repair_type: undefined,
  }

export class GeckoLegacyBoilerRepairRegion extends GeckoEditableFeature {
  readonly name: string
  readonly type = GeckoFeatureModelType.LegacyBoilerRepairRegion
  readonly geometry: GeckoComponentRect
  readonly wallNominalThickness: GeckoMeasurement | undefined
  readonly coatingNominalThickness: GeckoMeasurement | undefined
  readonly installationDate: Date | undefined
  readonly repairType: LegacyBoilerRepairType | undefined

  /**
   * Private constructor, allowing for a factory method
   *
   * NOTE: the json data should be stored with tube numbers as ints, but the class
   *       will store them as ±0.5 so that they cover the bin-bounds
   * @private
   */
  constructor(
    id: string,
    name: string,
    geometry: GeckoComponentRect,
    wallNominalThickness: GeckoMeasurement | undefined,
    coatingNominalThickness: GeckoMeasurement | undefined,
    installationDate: Date | undefined,
    repairType: LegacyBoilerRepairType | undefined
  ) {
    validateTubeMeasurement(
      geometry.x.start,
      id,
      'GeckoLegacyBoilerRepairRegion.geometry.x.start'
    )
    validateTubeMeasurement(
      geometry.x.end,
      id,
      'GeckoLegacyBoilerRepairRegion.geometry.x.end'
    )

    super(id)
    this.name = name
    this.geometry = geometry
    this.wallNominalThickness = wallNominalThickness
    this.coatingNominalThickness = coatingNominalThickness
    this.installationDate = installationDate
    this.repairType = repairType
  }

  public static CreateFromJSON(
    componentID: string,
    repairID: string,
    repairJSON: GeckoLegacyBoilerRepairRegionJSON
  ): GeckoLegacyBoilerRepairRegion {
    const repairGeometryData = repairJSON.feature_geometry as Record<
      string,
      number
    >

    const repairGeometry = GeckoComponentRect.CreateFromXYs(
      componentID,
      repairGeometryData.tube_start,
      repairGeometryData.y_start as number,
      repairGeometryData.tube_end,
      repairGeometryData.y_end as number,
      GeckoMeasurementUnit.Tubes,
      GeckoMeasurementUnit.Meters
    )

    const surfaceSpecs =
      repairJSON.surface_specs as GeckoLegacyBoilerRepairRegionJSON['surface_specs']
    const wallNominalThickness =
      surfaceSpecs.wall_nominal_thickness !== undefined
        ? GeckoMeasurement.CreateFromValue(
            surfaceSpecs.wall_nominal_thickness as number,
            GeckoMeasurementUnit.Meters
          )
        : undefined
    const coatingNominalThickness =
      surfaceSpecs.coating_nominal_thickness !== undefined
        ? GeckoMeasurement.CreateFromValue(
            surfaceSpecs.coating_nominal_thickness as number,
            GeckoMeasurementUnit.Meters
          )
        : undefined

    const installationDate = parseDate(repairJSON.installation_date)
    const repairName = parseString(repairJSON.name) ?? ''
    const repairType = repairJSON.repair_type as
      | LegacyBoilerRepairType
      | undefined

    return new GeckoLegacyBoilerRepairRegion(
      repairID,
      repairName,
      repairGeometry,
      wallNominalThickness,
      coatingNominalThickness,
      installationDate,
      repairType
    )
  }

  public static CreateMapFromJSON(
    componentID: string,
    recordsJSON: Record<string, GeckoLegacyBoilerRepairRegionJSON>
  ): Map<string, GeckoLegacyBoilerRepairRegion> {
    return Object.keys(recordsJSON).reduce((resultObj, repairID) => {
      resultObj.set(
        repairID,
        GeckoLegacyBoilerRepairRegion.CreateFromJSON(
          componentID,
          repairID,
          recordsJSON[repairID]
        )
      )
      return resultObj
    }, new Map<string, GeckoLegacyBoilerRepairRegion>())
  }

  asJSON() {
    return generateRepairRegion(
      this.name,
      this.geometry,
      this.wallNominalThickness,
      this.coatingNominalThickness,
      this.installationDate,
      this.repairType
    )
  }
}

export function generateRepairRegion(
  name: string,
  geometry: GeckoComponentRect,
  wallNominalThickness: GeckoMeasurement | undefined,
  coatingNominalThickness: GeckoMeasurement | undefined,
  installation_date: Date | undefined,
  repairType: LegacyBoilerRepairType | undefined
): GeckoLegacyBoilerRepairRegionJSON {
  validateTubeMeasurement(
    geometry.x.start,
    geometry.componentID,
    'GeckoLegacyBoilerRepairRegion.geometry.x.start'
  )
  validateTubeMeasurement(
    geometry.x.end,
    geometry.componentID,
    'GeckoLegacyBoilerRepairRegion.geometry.x.end'
  )

  const geometryInMeters = geometry.convertTo(
    GeckoMeasurementUnit.Tubes,
    GeckoMeasurementUnit.Meters
  )
  const repairRegionJSON: GeckoLegacyBoilerRepairRegionJSON = {
    type: GeckoFeatureModelType.LegacyBoilerRepairRegion,
    name: name,
    feature_geometry: {
      tube_start: geometryInMeters.x.start.num,
      y_start: geometryInMeters.y.start.num,
      tube_end: geometryInMeters.x.end.num,
      y_end: geometryInMeters.y.end.num,
    },
    surface_specs: {
      wall_nominal_thickness: undefined,
      coating_nominal_thickness: undefined,
    },
    installation_date: undefined,
    repair_type: undefined,
  }
  if (wallNominalThickness !== undefined) {
    repairRegionJSON.surface_specs.wall_nominal_thickness =
      wallNominalThickness.toNumber(GeckoMeasurementUnit.Meters)
  }
  if (coatingNominalThickness !== undefined) {
    repairRegionJSON.surface_specs.coating_nominal_thickness =
      coatingNominalThickness.toNumber(GeckoMeasurementUnit.Meters)
  }

  if (installation_date !== undefined) {
    repairRegionJSON.installation_date = installation_date
      .toISOString()
      .split('T')[0]
  }

  if (repairType !== undefined) {
    repairRegionJSON.repair_type = repairType
  }

  return repairRegionJSON
}

export const LegacyBoilerRepairRegionFormFields = (
  repairJSON: GeckoLegacyBoilerRepairRegionJSON
) => {
  return [
    {
      dataKey: 'name',
      label: 'Repair Name',
      value: repairJSON.name,
      unit_magnitude: null,
      initialValue: 'Repair Region',
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadOnly,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    installationDateFormField(repairJSON.installation_date, false),
    {
      dataKey: 'wall_nominal_thickness',
      label: 'New Wall Nominal Thickness',
      value: repairJSON.surface_specs.wall_nominal_thickness,
      unit_magnitude: UnitMagnitude.InchesOrMillimeters,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadOnly,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'coating_nominal_thickness',
      label: 'New Coating Nominal Thickness',
      value: repairJSON.surface_specs.coating_nominal_thickness,
      unit_magnitude: UnitMagnitude.InchesOrMillimeters,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadOnly,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'tube_start',
      label: 'Start Tube (Inclusive)',
      value: repairJSON.feature_geometry.tube_start,
      unit_magnitude: null,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadOnly,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'tube_end',
      label: 'End Tube (Inclusive)',
      value: repairJSON.feature_geometry.tube_end,
      unit_magnitude: null,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadOnly,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'y_start',
      label: 'Y Start',
      value: repairJSON.feature_geometry.y_start,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadOnly,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'y_end',
      label: 'Y End',
      value: repairJSON.feature_geometry.y_end,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadOnly,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'repair_type',
      label: 'Repair Type',
      value: repairJSON.repair_type,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      options: [
        {
          value: LegacyBoilerType.Overlay,
          label: 'Overlay',
        },
        {
          value: LegacyBoilerType.Panel,
          label: 'Panel',
        },
        {
          value: LegacyBoilerType.Unknown,
          label: 'Unknown',
        },
      ],
      externalUserAccess: ExternalUserFieldAccess.ReadOnly,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
  ]
}

export const LegacyBoilerRepairRegionJSONFromFormFields = (
  formFields: FormField[]
) => {
  const wallNominalThickness = getFormFieldByDataKey(
    formFields,
    'wall_nominal_thickness'
  )
  const coatingNominalThickness = getFormFieldByDataKey(
    formFields,
    'coating_nominal_thickness'
  )
  return generateRepairRegion(
    getFormFieldByDataKey(formFields, 'name')?.value as string,
    GeckoComponentRect.CreateFromXYs(
      'fake_component_id',
      getFormFieldByDataKey(formFields, 'tube_start')?.value as number,
      getFormFieldByDataKey(formFields, 'y_start')?.value as number,
      getFormFieldByDataKey(formFields, 'tube_end')?.value as number,
      getFormFieldByDataKey(formFields, 'y_end')?.value as number,
      GeckoMeasurementUnit.Meters,
      GeckoMeasurementUnit.Meters
    ),
    wallNominalThickness?.value !== undefined
      ? GeckoMeasurement.CreateFromValue(
          wallNominalThickness?.value as number,
          GeckoMeasurementUnit.Meters
        )
      : undefined,
    coatingNominalThickness?.value !== undefined
      ? GeckoMeasurement.CreateFromValue(
          coatingNominalThickness?.value as number,
          GeckoMeasurementUnit.Meters
        )
      : undefined,
    parseDate(getFormFieldByDataKey(formFields, 'installation_date')?.value),
    parseString(
      getFormFieldByDataKey(formFields, 'repair_type')?.value
    )?.toLowerCase() as LegacyBoilerRepairType | undefined
  )
}

export const LegacyBoilerRepairRegionFeatureTableOptions: FeatureTableOptions =
  {
    type: GeckoFeatureModelType.LegacyBoilerRepairRegion,
    title: 'Legacy Boiler Repair',
    singularName: 'Legacy Boiler Repair',
    fields: ['tube_start', 'tube_end', 'y_start', 'y_end'],
  }
